import DayPicker, {LocaleUtils} from "react-day-picker";
import LinedInput, {InputType} from "../LinedInput";
import masks from "../../constants/masks";
import React, {useState, useRef, useEffect} from "react";
import styles from "./CalendarInput.module.css";
import "react-day-picker/lib/style.css";

type Props = Omit<React.ComponentProps<"input">, "value"> & {
    id: string;
    label: string;
    value: Date | null;
    setValue: (d: Date) => void;
    validationMessage?: string[];
};

const MAX_DAY_RANGE = 14;

function getMaxDate(): Date {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + MAX_DAY_RANGE);
    return maxDate;
}

type NavbarElementProps = {
    previousMonth: Date;
    nextMonth: Date;
    onPreviousClick: () => void;
    onNextClick: () => void;
};

function Navbar(props: NavbarElementProps) {
    const months = LocaleUtils.getMonths();
    const prev = months[props.previousMonth.getMonth()];
    const next = months[props.nextMonth.getMonth()];

    return (
        <div className={styles.buttons}>
            <button className={styles.leftArrow} onClick={() => props.onPreviousClick()}>
                {prev.slice(0, 3)}
            </button>
            <button className={styles.rightArrow} onClick={() => props.onNextClick()}>
                {next.slice(0, 3)}
            </button>
        </div>
    );
}

function CalendarInput(
    {setValue, value, ...rest}: Props,
    ref: React.ForwardedRef<HTMLInputElement>,
) {
    const [calendarVisible, setCalendarVisible] = useState(false);
    const calendarRef = useRef<DayPicker | null>(null);
    const inputContainerRef = useRef<HTMLDivElement | null>(null);
    const currentDate = new Date();
    const maxDate = getMaxDate();

    useEffect(() => {
        function handleOutsideClick(e: Event) {
            // NOTE: type casting bc TS can't confirm e.target will always be a Node
            // https://stackoverflow.com/questions/43842057/detect-if-click-was-inside-react-component-or-not-in-typescript
            if (
                calendarRef.current &&
                !calendarRef.current.dayPicker.contains(e.target as Node) &&
                inputContainerRef.current &&
                !inputContainerRef.current.contains(e.target as Node)
            ) {
                setCalendarVisible(false);
            }
        }

        document.addEventListener("mousedown", handleOutsideClick);
        () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    function onSelectDate(day: Date) {
        const selectedTime = day.getTime();
        const currentDateStart = new Date().setUTCHours(0, 0, 0, 0);
        const maxDateEnd = getMaxDate().setUTCHours(23, 59, 59, 999);
        if (selectedTime >= currentDateStart && selectedTime <= maxDateEnd) {
            setValue(day);
            setCalendarVisible(false);
        }
    }

    return (
        <div className={styles.container}>
            <div ref={inputContainerRef}>
                <LinedInput
                    {...rest}
                    value={
                        value
                            ? value.toLocaleString("en-US", {
                                  month: "2-digit",
                                  day: "2-digit",
                                  year: "numeric",
                              })
                            : ""
                    }
                    ref={ref}
                    type={InputType.Text}
                    onFocus={() => setCalendarVisible(true)}
                    minLength={masks.dateOfBirth.pattern.length}
                    maxLength={masks.dateOfBirth.pattern.length}
                    pattern={masks.dateOfBirth.regex}
                    autoComplete="off"
                />
            </div>
            {calendarVisible ? (
                <DayPicker
                    modifiers={{
                        disabled: {before: currentDate, after: maxDate},
                    }}
                    selectedDays={value ?? currentDate}
                    ref={calendarRef}
                    className={styles.calendar}
                    onDayClick={onSelectDate}
                    navbarElement={Navbar}
                />
            ) : null}
        </div>
    );
}

export default React.forwardRef<HTMLInputElement, Props>(CalendarInput);
